import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { ToggleStatusPayload } from '@/services/charges/charge.mutation';
import { Charges } from '@/services/charges/charges.types';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import ManageChargeCategoryForm from '../ManageChargeCategory';
import { TabType } from './helper';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenChargesProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

enum status {
  Active = 'A',
}

export function getColumns(
  onOpen: OnOpenProps,
  activeTab: TabType,
  agentType: string,
  mutate: (payload: ToggleStatusPayload) => Promise<unknown>,
  t: (key: string) => string,
  openConfirmation: OpenChargesProps,
  defaultChargeType?: string,
): ColumnDef<Charges>[] {
  return [
    {
      id: 'chargeCategoryId',
      accessorKey: 'chargeCategoryId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => <div className="font-medium">{getSerialNumber(table, row.index)}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'categoryName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.categoryType')} />
      ),
      cell: ({ row }) => {
        const value = row.getValue<string>('categoryName');
        return row.original.status === 'A' ? (
          <Link
            params={{
              categoryId: row.getValue<string>('chargeCategoryId'),
            }}
            search={(previous) => ({
              activeTab,
              agentType: defaultChargeType,
              categoryName: row.original.categoryName,
              createdDate: previous.createdDate,
              breadcrumbReturnTo: window.location.href,
            })}
            to="/configuration-management/charges/$categoryId"
          >
            <div className={`font-medium truncate max-w-[20rem] text-link underline`}>{value}</div>
          </Link>
        ) : (
          <div className={`font-medium truncate max-w-[20rem] text-link`}>{value}</div>
        );
      },
      meta: {
        label: 'Category Type',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.description')} />
      ),
      cell: ({ row }) => <div className="w-[372px] break-all">{row.getValue('description')}</div>,
      meta: {
        label: 'Description',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'createdBy',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdBy')} />,
      cell: ({ row }) => <div>{row.getValue('createdBy')}</div>,
      meta: {
        label: 'Created By',
      },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue<Date>('createdDate'));
        return <div className="font-medium whitespace-nowrap">{formatDate(date, 'YYYY/MM/DD')}</div>;
      },
      meta: {
        label: 'Created Date',
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {row.getValue('status') === status.Active ? (
              <div className="text-success">{t('common.page.table.active')}</div>
            ) : (
              <div className="text-destructive-text">{t('common.page.table.blocked')}</div>
            )}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          <Can rule={{ allOf: [PERMS.charge.detail.view] }}>
            {row.getValue('status') === status.Active && (
              <EditIcon
                className="text-foreground-subtle size-4 hover:cursor-pointer"
                onClick={() =>
                  onOpen({
                    title: 'Manage Commission',
                    okText: t('common.page.modal.submit'),
                    size: 'lg',
                    modalTitle: 'configurationManagement.charges.assignChargeModal.manageCharge',
                    component: (
                      <ManageChargeCategoryForm
                        chargeCategoryId={row.getValue('chargeCategoryId')}
                        activeTab={activeTab}
                      />
                    ),
                  })
                }
              />
            )}
          </Can>
          <Can rule={{ allOf: [PERMS.charge.statusUpdate] }}>
            <Button
              variant={'ghost'}
              className="p-0"
              onClick={() => {
                const isActive = row.getValue('status') === status.Active;
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: isActive
                    ? t('configurationManagement.charges.blockUnblockModal.blockContent')
                    : t('configurationManagement.charges.blockUnblockModal.unblockContent'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  size: 'lg',
                  onAction: async () => {
                    await mutate({
                      chargeCategoryId: row.getValue('chargeCategoryId'),
                      status: row.getValue('status') === 'A' ? 'B' : 'A',
                      agentType: agentType,
                    });
                  },
                  onCancel: () => {},
                  variant: 'warning',
                });
              }}
            >
              {row.getValue('status') === status.Active ? (
                <LockCloseIcon className="text-destructive-text size-4 " />
              ) : (
                <LockOpenIcon className="text-link size-4 " />
              )}
            </Button>
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
